/**
 * BlogEditModal - Shared modal for creating/editing blog posts
 * Used by both Blog.js and Home.js pages
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiEye, FiEyeOff, FiStar, FiCircle, FiUpload, FiRss, FiCalendar, FiSend } from 'react-icons/fi';
import EditableModal from './EditableModal';
import RichTextEditor from './RichTextEditor';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const BlogEditModal = ({
  isOpen,
  isCreating,
  onClose,
  onSave,
  formData,
  updateFormData
}) => {
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showRSSLink, setShowRSSLink] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  // Fetch categories from config
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/config/blog_categories');
        if (res.data.success && res.data.config) {
          setCategories(res.data.config.values || []);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
        // Fallback to default categories
        setCategories(['Technology', 'Web Development', 'Education', 'Innovation', 'Leadership', 'Tutorial']);
      }
    };
    fetchCategories();
  }, []);

  // Set scheduled date from metadata
  useEffect(() => {
    if (formData.metadata?.scheduledPublishDate) {
      const date = new Date(formData.metadata.scheduledPublishDate);
      setScheduledDate(date.toISOString().slice(0, 16));
    } else {
      setScheduledDate('');
    }
  }, [formData.metadata?.scheduledPublishDate]);

  // Handle markdown file upload
  const handleMarkdownUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.md') && !file.name.endsWith('.markdown')) {
      showToast('Please upload a .md or .markdown file', 'error');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('markdown', file);
      formData.append('convertToHtml', 'false'); // Keep as markdown

      const res = await api.post('/upload/markdown', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data.success && res.data.data?.blogPost) {
        const blogPost = res.data.data.blogPost;
        // Populate form with parsed data
        updateFormData({
          ...blogPost,
          _id: formData._id // Preserve existing ID if editing
        });
        showToast('Markdown file uploaded and parsed successfully!', 'success');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to upload markdown file', 'error');
      console.error('Markdown upload error:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle publish now
  const handlePublishNow = async () => {
    const updatedData = {
      ...formData,
      metadata: {
        ...formData.metadata,
        published: true,
        scheduledPublishDate: null // Clear scheduled date if publishing now
      }
    };
    updateFormData(updatedData);
    // Save will be handled by the parent component's onSave
    // Just show RSS link and notification
    setShowRSSLink(true);
    showToast('Blog post will be published when you save!', 'success');
  };

  // Handle scheduled publishing
  const handleSchedulePublish = () => {
    if (!scheduledDate) {
      showToast('Please select a date and time for scheduled publishing', 'warning');
      return;
    }
    const date = new Date(scheduledDate);
    if (date <= new Date()) {
      showToast('Scheduled date must be in the future', 'warning');
      return;
    }
    updateFormData({
      ...formData,
      metadata: {
        ...formData.metadata,
        scheduledPublishDate: date.toISOString(),
        published: false // Keep as draft until scheduled time
      }
    });
    showToast(`Post scheduled for ${date.toLocaleString()}`, 'success');
  };

  return (
    <EditableModal
      isOpen={isOpen}
      isCreating={isCreating}
      onClose={onClose}
      onSave={onSave}
      title={isCreating ? 'Create Blog Post' : 'Edit Blog Post'}
      size="large"
    >
      <label>Title *</label>
      <input
        type="text"
        value={formData.title || ''}
        onChange={(e) => updateFormData({ title: e.target.value })}
        placeholder="Enter blog post title..."
        required
      />

      <label>Category *</label>
      <select
        value={formData.category || ''}
        onChange={(e) => updateFormData({ category: e.target.value })}
        required
      >
        <option value="">Select a category</option>
        {categories.map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>

      <label>Excerpt (for listings)</label>
      <textarea
        value={formData.data?.excerpt || ''}
        onChange={(e) => updateFormData({
          data: { ...formData.data, excerpt: e.target.value }
        })}
        placeholder="Brief summary of the post..."
        rows="3"
        maxLength="500"
      />

      <UploadSection>
        <label>Upload Markdown File</label>
        <UploadButton
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <FiUpload /> {uploading ? 'Uploading...' : 'Upload .md File'}
        </UploadButton>
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.markdown"
          onChange={handleMarkdownUpload}
          style={{ display: 'none' }}
        />
        <small>Upload a markdown file with frontmatter to auto-populate fields</small>
      </UploadSection>

      <label>Content *</label>
      <RichTextEditor
        value={formData.data?.content || ''}
        onChange={(content) => updateFormData({
          data: { ...formData.data, content }
        })}
        placeholder="Write your blog post content..."
        height="400px"
      />

      <label>Tags (comma-separated)</label>
      <input
        type="text"
        value={formData.data?.tags?.join(', ') || ''}
        onChange={(e) => updateFormData({
          data: {
            ...formData.data,
            tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
          }
        })}
        placeholder="e.g., javascript, react, tutorial"
      />

      <FormRow>
        <FormGroup>
          <label>Author</label>
          <input
            type="text"
            value={formData.data?.author || 'Richard Plotka'}
            onChange={(e) => updateFormData({
              data: { ...formData.data, author: e.target.value }
            })}
          />
        </FormGroup>

        <FormGroup>
          <label>Read Time (minutes)</label>
          <input
            type="number"
            min="1"
            max="60"
            value={formData.data?.readTime || 5}
            onChange={(e) => updateFormData({
              data: { ...formData.data, readTime: parseInt(e.target.value) || 5 }
            })}
          />
        </FormGroup>
      </FormRow>

      <PublishSection>
        <ToggleButtonGroup>
          <label>Published (visible to public)</label>
          <ToggleButton
            type="button"
            $active={formData.metadata?.published !== false}
            onClick={() => updateFormData({
              metadata: { ...formData.metadata, published: !(formData.metadata?.published !== false) }
            })}
          >
            <IconWrapper>
              {formData.metadata?.published !== false ? <FiEye size={20} /> : <FiEyeOff size={20} />}
            </IconWrapper>
            <ButtonText>
              {formData.metadata?.published !== false ? 'Published' : 'Draft'}
            </ButtonText>
          </ToggleButton>
        </ToggleButtonGroup>

        <PublishActions>
          <PublishNowButton
            type="button"
            onClick={handlePublishNow}
            disabled={!formData.title || !formData.category || !formData.data?.content}
          >
            <FiSend /> Publish Now
          </PublishNowButton>
        </PublishActions>

        <ScheduleSection>
          <label>Schedule Publishing</label>
          <ScheduleInput
            type="datetime-local"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
          />
          <ScheduleButton
            type="button"
            onClick={handleSchedulePublish}
            disabled={!scheduledDate}
          >
            <FiCalendar /> Schedule
          </ScheduleButton>
          {formData.metadata?.scheduledPublishDate && (
            <ScheduledInfo>
              Scheduled for: {new Date(formData.metadata.scheduledPublishDate).toLocaleString()}
            </ScheduledInfo>
          )}
        </ScheduleSection>

        {showRSSLink && (
          <RSSLinkSection>
            <RSSLink href={`${process.env.REACT_APP_API_BASE || 'http://localhost:5001'}/api/feed/rss`} target="_blank" rel="noopener noreferrer">
              <FiRss /> View RSS Feed
            </RSSLink>
          </RSSLinkSection>
        )}
      </PublishSection>

      <ToggleButtonGroup>
        <label>Highlight on Home Page</label>
        <ToggleButton
          type="button"
          $active={formData.metadata?.page === 'home' && formData.metadata?.section === 'blog'}
          onClick={() => {
            const isCurrentlyHighlighted = formData.metadata?.page === 'home' && formData.metadata?.section === 'blog';
            if (isCurrentlyHighlighted) {
              // Remove page and section from metadata when deactivating
              const { page: _page, section: _section, ...restMetadata } = formData.metadata || {};
              updateFormData({
                metadata: restMetadata
              });
            } else {
              // Add page and section to metadata when activating
              updateFormData({
                metadata: {
                  ...formData.metadata,
                  page: 'home',
                  section: 'blog'
                }
              });
            }
          }}
        >
          <IconWrapper>
            {formData.metadata?.page === 'home' && formData.metadata?.section === 'blog' ? <FiStar size={20} /> : <FiCircle size={20} />}
          </IconWrapper>
          <ButtonText>
            {formData.metadata?.page === 'home' && formData.metadata?.section === 'blog' ? 'Featured' : 'Not Featured'}
          </ButtonText>
        </ToggleButton>
      </ToggleButtonGroup>
    </EditableModal>
  );
};

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;

  label {
    font-weight: 500;
    color: var(--text-primary);
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
  
  ${props => props.$active ? `
    background: var(--accent-gradient);
    color: white;
    border: 2px solid var(--accent-primary);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    }
  ` : `
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 2px solid var(--border-color);
    
    &:hover {
      border-color: var(--accent-primary);
      color: var(--text-primary);
    }
  `}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  
  svg {
    display: block;
  }
`;

const ButtonText = styled.span`
  font-weight: 600;
  line-height: 1;
`;

const UploadSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 0.5rem;
  border: 1px dashed var(--border-color);

  label {
    display: block;
    margin-bottom: 0.5rem;
  }

  small {
    display: block;
    margin-top: 0.5rem;
    color: var(--text-secondary);
    font-size: 0.875rem;
  }
`;

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PublishSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid var(--border-color);
`;

const PublishActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const PublishNowButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScheduleSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 0.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
  }
`;

const ScheduleInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 1rem;
  margin-bottom: 0.75rem;
`;

const ScheduleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--accent-primary);
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScheduledInfo = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 0.375rem;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 500;
`;

const RSSLinkSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 0.5rem;
  border: 1px solid rgba(16, 185, 129, 0.3);
`;

const RSSLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #10b981;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    color: #059669;
    text-decoration: underline;
  }
`;

export default BlogEditModal;
